import Flex from "@ui-kit/layout/Flex/Flex.web";
import Box from "@ui-kit/layout/Box/Box.web";
import {
  PatronTypeSerializer,
  TicketTypeSerializer,
} from "@app-contexts/public/features/event-detail/data/types/events";
import { useMemo } from "react";
import { TicketTypePatronItemsContainerProps } from "./TicketTypePatronItemsContainer.props";
import PatronItemSelection from "../PatronItemSelection/PatronItemSelection.web";

/**
 * TicketTypePatronItemsContainer allows for the selection of TicketTypes based on their PatronType.
 * It groups TicketTypes by their PatronType and sorts the PatronTypes by the lowest price of their TicketTypes.
 *
 * This component can be imagined as a bridge between a list of TTs all the way through an ItemQuantitySelectInfo.
 *
 * ---
 * as a visual aid:
 *
 * __TicketTypes - Regular__
 *
 * [tt1, tt2] -> TicketTypesList -> TicketTypesContainer -> ItemQuantitySelectInfo
 *
 * __TicketTypes - With `patron_type`__
 *
 * [tt1, tt2] -> TicketTypesList -> TicketTypePatronItemsContainer -> PatronItemSelection -> TicketTypesContainer -> ItemQuantitySelectInfo
 *
 */
function TicketTypePatronItemsContainer({
  ticketTypes,
  event,
  basketVoucherCode,
  holdDetailsData,
  initialSelectedTicketTypes,
  onAmountRequested,
  ticketsAuthorizedByPassword,
  ticketAuthArray,
  updateTicketAuthArray,
}: TicketTypePatronItemsContainerProps) {
  /**
   * Map of PatronType ids to their respective TicketTypes.
   */
  const patronTypeTicketTypesMap = useMemo(() => {
    type PatronTypeId = number;

    const map = new Map<
      PatronTypeId,
      {
        patronType: PatronTypeSerializer;
        ticketTypes: Array<TicketTypeSerializer>;
      }
    >();

    // Assuming ticketTypes is an array of TicketTypeSerializer objects
    ticketTypes.forEach((ticketType) => {
      const patronTypeId = ticketType.patron_type?.id;
      if (
        ticketType.patron_type != null &&
        typeof patronTypeId !== "undefined"
      ) {
        if (!map.has(patronTypeId)) {
          // Create a new entry in the map for this patron type if it doesn't exist
          map.set(patronTypeId, {
            patronType: ticketType.patron_type,
            ticketTypes: [],
          });
        }
        // Push the ticket type into the appropriate patron type entry
        map.get(patronTypeId)?.ticketTypes.push(ticketType);
      }
    });

    return map;
  }, [ticketTypes]);

  /**
   * Map of PatronType ids to the lowest price of its own TicketTypes.
   */
  const patronTypeTicketTypesLowestPriceMap = useMemo(() => {
    // for each patron type, get the lowest price of its ticket types
    type PatronTypeId = number;
    type PatronTypeTTLowestPrice = string;

    const patronTypeToPrice = new Map<PatronTypeId, PatronTypeTTLowestPrice>();
    patronTypeTicketTypesMap.forEach((value, key) => {
      const lowestPriceNumber = Math.min(
        ...value.ticketTypes.map((tt) => parseFloat(tt.price as string))
      );
      const lowestPricedTT = value.ticketTypes.find(
        (tt) => parseFloat(tt.price as string) === lowestPriceNumber
      );
      // Keep track of the patron type ids and the lowest price of its ticket types
      patronTypeToPrice.set(key, lowestPricedTT?.price as string);
    });
    return patronTypeToPrice;
  }, [patronTypeTicketTypesMap]);

  /**
   * Array of PatronType ids sorted by the TicketType.sort_order
   */
  const sortedPatronTicketTypesMapKeys = useMemo(() => {
    // Sort by TicketType.sort_order
    const sortedPatronTypeEntries = Array.from(
      patronTypeTicketTypesMap.entries()
    ).sort(([, aValue], [, bValue]) => {
      const aSortOrder = aValue.ticketTypes[0].sort_order ?? 0;
      const bSortOrder = bValue.ticketTypes[0].sort_order ?? 0;
      return aSortOrder - bSortOrder;
    });

    // Extract the keys (PatronTypeIds) from the sorted entries
    return sortedPatronTypeEntries.map(([key]) => key);
  }, [patronTypeTicketTypesMap]);

  return (
    <Flex flexDirection="column" gap="xl">
      {
        // Iterate over the sorted array of PatronType ids and render a PatronItemSelection for each,
        // referencing the patronTypeTicketTypesMap to get the details of the PatronType and its TicketTypes.
        sortedPatronTicketTypesMapKeys.map((patronTypeId) => {
          const patronType = patronTypeTicketTypesMap.get(
            Number(patronTypeId)
          )?.patronType;
          const patronTypeLowestPrice = patronTypeTicketTypesLowestPriceMap.get(
            Number(patronTypeId)
          );
          const ticketTypes = patronTypeTicketTypesMap.get(
            Number(patronTypeId)
          )?.ticketTypes;
          return (
            <Box
              backgroundColor="bg"
              borderRadius={{ base: "none", sm: "md" }}
              key={patronTypeId}
            >
              <PatronItemSelection
                title={patronType?.label ?? ""}
                items={ticketTypes ?? []}
                event={event}
                basketVoucherCode={basketVoucherCode}
                holdDetailsData={holdDetailsData}
                startingFromPrice={patronTypeLowestPrice ?? "0.00"}
                initialSelectedItems={initialSelectedTicketTypes}
                onAmountRequested={onAmountRequested}
                ticketTypesPatronTypeCount={ticketTypes?.length ?? 0}
                ticketsAuthorizedByPassword={ticketsAuthorizedByPassword}
                ticketAuthArray={ticketAuthArray}
                updateTicketAuthArray={updateTicketAuthArray}
              />
            </Box>
          );
        })
      }
    </Flex>
  );
}

export default TicketTypePatronItemsContainer;
